import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';

const quizQuestions = [
  {
    question: "What is psychological safety in the workplace?",
    options: [
      "Feeling safe to express ideas and concerns",
      "Never sharing feedback",
      "Ignoring team members",
      "Working alone always"
    ],
    correctAnswer: 0,
    explanation: "Psychological safety means feeling safe to express ideas and concerns."
  },
  {
    question: "Which of the following is part of the '3C Rule' for workplace health?",
    options: [
      "Clarity",
      "Communication",
      "Care",
      "All of the above"
    ],
    correctAnswer: 3,
    explanation: "The '3C Rule' includes Clarity, Communication, and Care."
  },
  {
    question: "How can you increase team positivity at work?",
    options: [
      "Thank one person at work every day",
      "Ignore your team",
      "Never give feedback",
      "Work alone"
    ],
    correctAnswer: 0,
    explanation: "Thanking one person at work every day increases team positivity."
  },
  {
    question: "What is one way to build a healthy work environment?",
    options: [
      "Communicate openly and show appreciation",
      "Ignore feedback",
      "Work alone",
      "Never say thank you"
    ],
    correctAnswer: 0,
    explanation: "Communicating openly and showing appreciation helps build a healthy work environment."
  },
  {
    question: "A positive work environment can help you:",
    options: [
      "Feel safer and more supported",
      "Become more isolated",
      "Work less effectively",
      "Ignore your team"
    ],
    correctAnswer: 0,
    explanation: "A positive work environment helps you feel safer and more supported."
  }
];

const OccupationalModule4QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { saveProgress } = useModuleProgress('occupationalM4');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (index: number) => {
    if (selectedAnswer === null) {
      setSelectedAnswer(index);
      setShowExplanation(true);
    }
  };

  const handleNext = async () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
  await saveProgress(quizQuestions.length, true); // Mark module as completed
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">You've completed the Module 4 quiz!</p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <ul className="achievement-list">
              <li><span className="check"></span> Understanding psychological safety</li>
              <li><span className="check"></span> Applying the 3C Rule</li>
              <li><span className="check"></span> Increasing team positivity</li>
            </ul>
          </div>
          <button className="resource-button" onClick={() => navigate('/panel/learn/occupational-resources')}>Return to Resources</button>
        </div>
      </div>
    );
  }

  const currentQuiz = quizQuestions[currentQuestion];
  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left occupational-panel">
          <div className="module-header">
            <h1>Module 4 Quiz</h1>
            <p className="module-subtitle">Building a Healthy Work Environment</p>
            <hr className="module-divider" />
            <p className="module-tool-info">Test your understanding of workplace health and team culture.</p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%` }}></div>
              </div>
              <span className="progress-text">Question {currentQuestion + 1} of {quizQuestions.length}</span>
            </div>
            <div className="question-section">
              <h2 className="question-title">{currentQuiz.question}</h2>
              <div className="answer-options">
                {currentQuiz.options.map((option, idx) => (
                  <label key={idx} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={idx}
                      checked={selectedAnswer === idx}
                      onChange={() => handleAnswerSelect(idx)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button className="btn-secondary" onClick={currentQuestion === 0 ? () => navigate('/panel/learn/occupational-resources') : handleBack}>
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Next Question</button>
              ) : (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Complete Quiz</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationalModule4QuizPage;


